/**
 * Variables de entorno (Vite, build time).
 * Solo configuración pública de Firebase — sin secretos de servidor.
 */

const firebaseKeys = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const satisfies readonly (keyof ImportMetaEnv)[];

export type FirebaseEnvConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

function read(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key];
  return typeof value === 'string' ? value.trim() : '';
}

/** true si todas las variables de Firebase están definidas. */
export function isFirebaseConfigured(): boolean {
  return firebaseKeys.every((key) => read(key) !== '');
}

/**
 * Config de Firebase. Lanza si falta alguna variable (usar tras isFirebaseConfigured).
 */
export function getFirebaseEnv(): FirebaseEnvConfig {
  const missing = firebaseKeys.filter((key) => read(key) === '');
  if (missing.length > 0) {
    throw new Error(
      `Firebase no configurado. Faltan: ${missing.join(', ')}. Ver .env.example`,
    );
  }
  return {
    apiKey: read('VITE_FIREBASE_API_KEY'),
    authDomain: read('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: read('VITE_FIREBASE_PROJECT_ID'),
    storageBucket: read('VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: read('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: read('VITE_FIREBASE_APP_ID'),
  };
}

export const env = {
  apiBaseUrl: read('VITE_API_BASE_URL'),
  useWorkerReservations: read('VITE_USE_WORKER_RESERVATIONS') === 'true',
} as const;
